import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import { StyledBox } from '../components/styledComponents/StyledItemComponents'
import { theme } from '../theme'

const warrantySteps = [
  {
    icon: <DescriptionOutlinedIcon />,
    title: 'Pastreaza documentele comenzii',
    text: 'Factura finala, proforma si documentele comenzii sunt importante pentru orice solicitare de garantie.'
  },
  {
    icon: <VerifiedUserOutlinedIcon />,
    title: 'Verifica garantia produsului',
    text: 'Garantia se acorda conform politicii producatorului si legislatiei aplicabile pentru produsul cumparat.'
  },
  {
    icon: <BuildCircleOutlinedIcon />,
    title: 'Activeaza extinderea, daca este eligibila',
    text: 'Pentru unele produse, garantia extinsa se activeaza pe portalul oficial al producatorului, folosind seria produsului si documentul de achizitie.'
  }
]

const supportDetails = [
  'Numarul comenzii',
  'Factura sau proforma',
  'Seria produsului, daca exista',
  'Poze sau descrierea defectului, pentru service'
]

const WarrantyStepCard = ({ step }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: '18px',
      border: '1px solid rgba(49,49,49,0.12)',
      backgroundColor: '#fff',
      boxShadow: '0 12px 36px rgba(29,29,27,0.06)'
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(226,6,19,0.09)',
            color: theme.palette.accent.main
          }}
        >
          {step.icon}
        </Box>
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 900, fontSize: '18px', mb: 1 }}>
            {step.title}
          </Typography>
          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.75 }}>
            {step.text}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
)

WarrantyStepCard.propTypes = {
  step: PropTypes.shape({
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired
  }).isRequired
}

const Garantii = () => {
  return (
    <Box sx={{ backgroundColor: theme.palette.secondary.main, minHeight: '70vh', py: { xs: 3, md: 5 } }}>
      <StyledBox sx={{ px: { xs: 2, md: 5 } }}>
        <Box
          sx={{
            borderRadius: { xs: '20px', md: '28px' },
            overflow: 'hidden',
            backgroundColor: theme.palette.primary.main,
            border: '1px solid rgba(49,49,49,0.12)',
            boxShadow: '0 18px 60px rgba(29,29,27,0.08)'
          }}
        >
          <Grid container>
            <Grid item xs={12} md={7}>
              <Stack spacing={3} sx={{ p: { xs: 3, md: 5 } }}>
                <Box>
                  <Typography
                    variant='overline'
                    sx={{
                      color: theme.palette.accent.main,
                      fontWeight: 900,
                      letterSpacing: '0.16em'
                    }}
                  >
                    Garantie Primusvest
                  </Typography>
                  <Typography
                    variant='h1'
                    sx={{
                      mt: 1,
                      fontSize: { xs: '34px', md: '50px' },
                      lineHeight: 1.05,
                      fontWeight: 900,
                      color: theme.palette.fontColor.dark
                    }}
                  >
                    Informatii despre garantie si extinderea garantiei
                  </Typography>
                  <Typography variant='body1' sx={{ mt: 2, color: theme.palette.fontColor.medium, lineHeight: 1.8 }}>
                    Produsele comercializate de Primusvest beneficiaza de garantie conform politicii producatorului si legislatiei aplicabile. Pentru produsele eligibile, garantia extinsa se activeaza separat, de regula pe portalul oficial al producatorului.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to='/profilulmeu#Orders'
                    variant='contained'
                    sx={{
                      backgroundColor: theme.palette.accent.main,
                      color: '#fff',
                      borderRadius: '999px',
                      px: 2.5,
                      py: 1.2,
                      fontWeight: 900,
                      textTransform: 'none',
                      boxShadow: '0 10px 24px rgba(226,6,19,0.28)',
                      '&:hover': { backgroundColor: '#b9000d' }
                    }}
                  >
                    Vezi comenzile mele
                  </Button>
                  <Button
                    component='a'
                    href='mailto:office@primusvest.ro?subject=Solicitare%20garantie%20Primusvest'
                    variant='outlined'
                    startIcon={<EmailOutlinedIcon />}
                    sx={{
                      borderColor: 'rgba(49,49,49,0.24)',
                      color: theme.palette.fontColor.dark,
                      borderRadius: '999px',
                      px: 2.5,
                      py: 1.2,
                      fontWeight: 900,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: theme.palette.accent.main,
                        backgroundColor: 'rgba(226,6,19,0.05)'
                      }
                    }}
                  >
                    Cere suport garantie
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  height: '100%',
                  minHeight: { xs: 260, md: 520 },
                  p: { xs: 3, md: 5 },
                  display: 'flex',
                  alignItems: 'stretch',
                  background: 'linear-gradient(145deg, #1d1d1b 0%, #313131 58%, #e20613 160%)'
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    width: '100%',
                    borderRadius: '22px',
                    backgroundColor: 'rgba(255,255,255,0.94)',
                    border: '1px solid rgba(255,255,255,0.32)'
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                    <Stack spacing={2.25}>
                      <Chip
                        label='Pentru clienti'
                        sx={{
                          width: 'fit-content',
                          backgroundColor: 'rgba(226,6,19,0.1)',
                          color: theme.palette.accent.main,
                          fontWeight: 900
                        }}
                      />
                      <Typography variant='h5' sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                        Ce trebuie sa ai pregatit pentru garantie
                      </Typography>
                      <Stack spacing={1.25}>
                        {supportDetails.map((detail) => (
                          <Stack key={detail} direction='row' spacing={1.2} alignItems='center'>
                            <AssignmentTurnedInOutlinedIcon sx={{ color: theme.palette.accent.main }} />
                            <Typography variant='body2' sx={{ color: theme.palette.fontColor.main, fontWeight: 700 }}>
                              {detail}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Divider />
                      <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.7 }}>
                        Daca nu esti sigur ce link de garantie extinsa trebuie folosit pentru un produs, trimite-ne numarul comenzii si te redirectionam catre pagina corecta a producatorului.
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2.5} sx={{ mt: 3 }}>
          {warrantySteps.map((step) => (
            <Grid item xs={12} md={4} key={step.title}>
              <WarrantyStepCard step={step} />
            </Grid>
          ))}
        </Grid>

        <Card
          elevation={0}
          sx={{
            mt: 3,
            borderRadius: '22px',
            border: '1px solid rgba(49,49,49,0.12)',
            backgroundColor: '#fff'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Typography variant='h5' sx={{ fontWeight: 900, mb: 1 }}>
                  Garantie extinsa
                </Typography>
                <Typography variant='body1' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.8 }}>
                  Extinderea garantiei nu se aplica automat pentru toate produsele. In functie de producator si model, poate fi necesara inregistrarea produsului intr-un termen limitat de la achizitie, folosind seria produsului si documentul de achizitie.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack spacing={1.25}>
                  <Typography variant='body1' sx={{ fontWeight: 900 }}>
                    Ai nevoie de ajutor?
                  </Typography>
                  <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, lineHeight: 1.7 }}>
                    Contacteaza echipa Primusvest si mentioneaza numarul comenzii. Iti spunem ce pasi trebuie urmati pentru produsul cumparat.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to='/contact'
                    endIcon={<OpenInNewOutlinedIcon />}
                    sx={{
                      width: 'fit-content',
                      px: 0,
                      color: theme.palette.accent.main,
                      fontWeight: 900,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: theme.palette.fontColor.dark
                      }
                    }}
                  >
                    Vezi datele de contact
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </StyledBox>
    </Box>
  )
}

export default Garantii
